'use strict';

/**
 * PostHog `before_send` and replay URL hooks that keep the Keycloak callback parameters out of
 * everything sent to PostHog.
 *
 * @module analytics.analyticsSanitize
 */

import { stripAuthParams, stripAuthParamsDeep } from '../monitoring/authParamsSanitize.js';

const SNAPSHOT_DATA_KEY = '$snapshot_data';
const HEATMAP_DATA_KEY = '$heatmap_data';
const RRWEB_PLUGIN_EVENT_TYPE = 6;
const RRWEB_CONSOLE_PLUGIN = 'rrweb/console@1';

function isPlainObject(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Replay data is mostly compressed and must stay byte-for-byte intact; console messages recorded
 * by the rrweb console plugin are the only part sent as plain text. Their shape is internal to
 * posthog-js: recheck it on every upgrade.
 */
function scrubRecordingEvent(event) {
  if (!isPlainObject(event) || event.type !== RRWEB_PLUGIN_EVENT_TYPE) {
    return event;
  }

  const data = event.data;

  if (!isPlainObject(data) || data.plugin !== RRWEB_CONSOLE_PLUGIN) {
    return event;
  }

  return Object.assign({}, event, {
    data: Object.assign({}, data, { payload: stripAuthParamsDeep(data.payload) }),
  });
}

function scrubHeatmapData(heatmapData) {
  const scrubbed = {};

  Object.keys(heatmapData).forEach(function (url) {
    const scrubbedUrl = stripAuthParams(url);
    const existing = scrubbed[scrubbedUrl];
    const points = heatmapData[url];

    scrubbed[scrubbedUrl] = Array.isArray(existing) && Array.isArray(points) ? existing.concat(points) : points;
  });

  return scrubbed;
}

function scrubValue(key, value) {
  if (key === SNAPSHOT_DATA_KEY) {
    return Array.isArray(value) ? value.map(scrubRecordingEvent) : scrubRecordingEvent(value);
  }

  if (key === HEATMAP_DATA_KEY && isPlainObject(value)) {
    return scrubHeatmapData(value);
  }

  return stripAuthParamsDeep(value);
}

function scrubProperties(properties) {
  const scrubbed = {};

  Object.keys(properties).forEach(function (key) {
    scrubbed[key] = scrubValue(key, properties[key]);
  });

  return scrubbed;
}

function stripAuthParamsFromCapture(capture) {
  if (!capture) {
    return capture;
  }

  return Object.assign({}, capture, {
    properties: scrubProperties(capture.properties),
    $set: capture.$set && scrubProperties(capture.$set),
    $set_once: capture.$set_once && scrubProperties(capture.$set_once),
  });
}

function stripAuthParamsFromRecordedUrl(request) {
  return typeof request.name === 'string'
    ? Object.assign({}, request, { name: stripAuthParams(request.name) })
    : request;
}

export { stripAuthParamsFromCapture, stripAuthParamsFromRecordedUrl };
